// FI-2181 build watcher: notices when a newer build is live and gets the
// page onto it without interrupting anyone.
//
// the app is a hash-mode spa, so navigating never refetches index.html;
// only window.location.reload() does. a tab left open across a deploy
// keeps running the old bundle until then. this module holds the pure
// decisions plus a tiny observable state; utils/build_watcher_install.js
// wires them to the router, timers and window, and
// components/fi_update_nudge.vue renders the banner.
//
//   CURRENT_BUILD  the id this bundle was compiled with (vue.config.js
//                  inlines VUE_APP_BUILD_ID from scripts/build_id.js);
//                  empty on a local serve, which turns everything off
//   version.json   {build, at} written next to index.html by
//                  scripts/postbuild_release.js, served no-cache
//
// three ways onto the new build, in order of preference:
//   navigation  the next route change reloads into the target route
//               (never when a page has unsaved work, never on a public
//               route, never for a same-path change, never under cypress)
//   nudge       after NUDGE_AFTER_MS on the same page a calm banner offers
//               "Refresh now" / "Later"; later is per build
//   board       chromeless display boards reload themselves once the new
//               build has been live a while, the viewer has been idle and
//               the page is old enough, with a per-tab jitter so a wall of
//               boards does not blink together (never a chromeless form or
//               a public route, never while a field has focus)
//
// everything that touches window or fetch takes it as a parameter or reads
// it lazily so the spec runs in mocha.
import Vue from 'vue'

export const CURRENT_BUILD = process.env.VUE_APP_BUILD_ID || ''

export const CHECK_INTERVAL_MS = 5 * 60 * 1000
export const FIRST_CHECK_DELAY_MS = 60 * 1000
// a navigation re-checks at most this often (afterEach)
export const NAV_CHECK_THROTTLE_MS = 60 * 1000
export const NUDGE_AFTER_MS = 10 * 60 * 1000
export const BOARD_STALE_MS = 5 * 60 * 1000
export const BOARD_IDLE_MS = 2 * 60 * 1000
export const BOARD_MIN_PAGE_AGE_MS = 10 * 60 * 1000
export const BOARD_JITTER_MAX_MS = 90 * 1000
// loop guard: a reload that did not pick up the new build (a cached shell,
// a half-finished deploy) waits this long before trying again
export const RELOAD_LOOP_BACKOFF_MS = 60 * 60 * 1000
export const RELOAD_ATTEMPT_KEY = 'fi_build_reload_attempt'

export const state = Vue.observable({
  // the build id version.json last reported, once it differs from ours
  liveBuild: null,
  // when we first learned a newer build was live (kept across further
  // newer builds: the page has been stale since then)
  newBuildSeenAt: null,
  // "Later" on the nudge, per build; a newer build re-shows it
  dismissedForBuild: null,
})

// ---- pure decisions -------------------------------------------------------

// false when either side is unknown: a local serve (empty CURRENT_BUILD)
// or a failed fetch must never look like a new build
export function isNewBuild(current, fetched) {
  return !!current && !!fetched && current !== fetched
}

export function reloadOnNavigateDecision({
  newBuild, dirty, isCypress, toName, toPath, fromPath, publicRouteNames,
}) {
  if (!newBuild || dirty || isCypress) return false
  if ((publicRouteNames || []).includes(toName)) return false
  // a query-only change (a filter, a tab) is the same page: no reload
  if (toPath === fromPath) return false
  return true
}

export function nudgeDue({ newBuildSeenAt, nowMs, dismissedForBuild, liveBuild }) {
  if (!liveBuild || !newBuildSeenAt) return false
  if (dismissedForBuild === liveBuild) return false
  return nowMs - newBuildSeenAt >= NUDGE_AFTER_MS
}

export function clampJitter(jitterMs) {
  const j = Number(jitterMs) || 0
  return Math.min(Math.max(j, 0), BOARD_JITTER_MAX_MS)
}

export function displayReloadDue({
  newBuild, isDisplay, newBuildSeenAt, lastInputAt, pageLoadedAt, nowMs, jitterMs,
}) {
  if (!newBuild || !isDisplay || !newBuildSeenAt) return false
  if (nowMs - newBuildSeenAt < BOARD_STALE_MS + clampJitter(jitterMs)) return false
  // no input ever recorded counts as idle
  if (lastInputAt && nowMs - lastInputAt < BOARD_IDLE_MS) return false
  if (nowMs - (pageLoadedAt || 0) < BOARD_MIN_PAGE_AGE_MS) return false
  return true
}

// chromeless is not the same as a board: the public erm and sos forms, the
// register pages and the sos dispatcher page render without chrome too, and
// a self-reload there would wipe a half-filled form. the public route names
// are excluded as on navigation, plus the chromeless forms behind a login
export const CHROMELESS_FORM_ROUTE_NAMES = [
  'direct_register', 'sos_register', 'ops_desk_public_form', 'sos_driver_form', 'sos_dispatch', 'sos_dispatch_token',
]

export function isDisplayBoardRoute({ chromeless, routeName, publicRouteNames }) {
  if (!chromeless) return false
  if ((publicRouteNames || []).includes(routeName)) return false
  return !CHROMELESS_FORM_ROUTE_NAMES.includes(routeName)
}

// a focused field means someone is mid-entry even with no recent keystroke
export function isEditableElement(el) {
  if (!el) return false
  const tag = String(el.tagName || '').toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return el.isContentEditable === true
}

// afterEach re-check throttle
export function checkDue({ lastCheckAt, nowMs, minGapMs = NAV_CHECK_THROTTLE_MS }) {
  return nowMs - (lastCheckAt || 0) >= minGapMs
}

// loop guard: the previous reload in this tab was attempted for
// `attemptedBuild`; if the origin still reports that build and we are still
// not running it, the reload did not take
export function reloadLoopDetected({ attemptedBuild, currentBuild, fetchedBuild }) {
  if (!attemptedBuild || !fetchedBuild) return false
  return attemptedBuild === fetchedBuild && fetchedBuild !== currentBuild
}

// record what version.json said. returns true when the state changed.
// `target` defaults to the module state; the spec passes its own object
export function noteFetchedBuild({ current, fetched, nowMs, target = state }) {
  if (!isNewBuild(current, fetched)) return false
  if (target.liveBuild === fetched) return false
  target.liveBuild = fetched
  if (!target.newBuildSeenAt) target.newBuildSeenAt = nowMs
  return true
}

export function hasNewBuild(target = state) {
  return isNewBuild(CURRENT_BUILD, target.liveBuild)
}

// ---- dirty registry -------------------------------------------------------

const dirtyChecks = new Set()

// pages with unsaved work register a predicate; a throwing predicate
// counts as dirty (better a missed reload than lost work)
export function registerDirtyCheck(fn) {
  if (typeof fn === 'function') dirtyChecks.add(fn)
}

export function unregisterDirtyCheck(fn) {
  dirtyChecks.delete(fn)
}

export function isAnyDirty() {
  for (const fn of dirtyChecks) {
    try {
      if (fn()) return true
    } catch (_) {
      return true
    }
  }
  return false
}

// ---- window-facing helpers, all injectable --------------------------------

function defaultWindow() {
  return typeof window !== 'undefined' ? window : null
}

// the e2e suite (cypress 3.4.1 against the live dev site) stubs requests
// with cy.route and would lose them on a reload: the watcher is off there
export function isCypress(win = defaultWindow()) {
  return !!(win && win.Cypress)
}

export function versionUrl(baseUrl) {
  const base = baseUrl || process.env.VUE_APP_BASE_URL || '/'
  return base.replace(/\/?$/, '/') + 'version.json'
}

// GET version.json straight through fetch: no networking/execute, no
// Authorization header, no cache. returns the build string or null and
// never throws
export async function fetchBuildId(fetchImpl) {
  const win = defaultWindow()
  const f = fetchImpl || (win && typeof win.fetch === 'function' ? win.fetch.bind(win) : null)
  if (!f) return null
  try {
    const res = await f(versionUrl(), { cache: 'no-store', credentials: 'omit' })
    if (!res || !res.ok) return null
    const body = await res.json()
    const build = body && body.build
    return typeof build === 'string' && build ? build : null
  } catch (_) {
    return null
  }
}

// FI-2266: reading window.sessionStorage itself throws (SecurityError) where
// the browser blocks site storage for the page; null is "no storage" to the
// reload-attempt helpers below
export function sessionStorageOf(win) {
  try {
    return win ? win.sessionStorage : null
  } catch (_) {
    return null
  }
}

// boot: read the previous reload attempt in this tab. a key that matches
// the running build means the reload took, so it is cleared; anything else
// is handed back for reloadLoopDetected once version.json has answered
export function readReloadAttempt(storage, currentBuild) {
  try {
    const attempted = storage ? storage.getItem(RELOAD_ATTEMPT_KEY) : null
    if (!attempted) return null
    if (attempted === currentBuild) {
      storage.removeItem(RELOAD_ATTEMPT_KEY)
      return null
    }
    return attempted
  } catch (_) {
    return null
  }
}

export function clearReloadAttempt(storage) {
  try {
    if (storage) storage.removeItem(RELOAD_ATTEMPT_KEY)
  } catch (_) { /* storage blocked */ }
}

let reloading = false

// true once reloadInto has been called on this page: the install-time
// router hook lets everything through from then on so the hashchange our
// hash write raises cannot bounce the url back before the reload lands
export function isReloading() {
  return reloading
}

// set the hash to the target route, then reload. a hash-only change never
// reloads by itself, and reload() alone would land on the current route.
// the attempted build is parked in sessionStorage for the loop guard
export function reloadInto(fullPath, win = defaultWindow()) {
  if (!win) return
  reloading = true
  try {
    win.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, state.liveBuild || '')
  } catch (_) { /* storage blocked */ }
  const target = String(fullPath || '/')
  win.location.hash = target.startsWith('#') ? target : '#' + target
  win.location.reload()
}

// spec hook: fresh state between cases
export function resetBuildWatcherState() {
  state.liveBuild = null
  state.newBuildSeenAt = null
  state.dismissedForBuild = null
  dirtyChecks.clear()
  reloading = false
}
